/**
 * CRM CSV Export Utilities
 *
 * Generic helpers for copying table data to clipboard and exporting to CSV.
 */

import { toast } from 'sonner';

/**
 * Escape a value for CSV, handling special characters and CSV injection
 */
export function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);

  // Prevent CSV injection (formulas starting with =, +, -, @, tab, or carriage return)
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }

  // If value contains comma, quote, or newline, wrap in quotes and escape inner quotes
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

interface ExportColumn<T> {
  label: string;
  getValue: (item: T) => string | number | null;
}

/**
 * Export items to a CSV file and trigger download
 */
export function exportToCSV<T>(
  items: T[],
  columns: ExportColumn<T>[],
  filename: string,
): void {
  const header = columns.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    columns.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );

  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  toast.success(`Exported ${items.length} rows`);
}

/**
 * Copy items to clipboard as tab-separated values (pasteable into spreadsheets)
 */
export function copyToClipboard<T>(
  items: T[],
  columns: ExportColumn<T>[],
): void {
  const header = columns.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    columns.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );

  navigator.clipboard.writeText([header, ...rows].join('\n'));
  toast.success('Copied to clipboard');
}
